import React, { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtom } from 'jotai';

import { useMessages } from '@consumer-frontend/api';
import { Container } from '@consumer-frontend/shared-ui';

import {
  accountIdAtom, messageCenterPaginationAtom, messageDetailPaginationAtom,
} from '../../store';
import { CnuLoadingSpinner } from '../CnuLoadingSpinner';
import { CnuMessageCenterListGroup } from '../CnuMessageCenterListGroup';
import { CnuPagination } from '../CnuPagination';
import { Error } from '../Error';

import '@consumer-bootstrap/index.scss';
import '@consumer-bootstrap/pages/account-home/message-center.scss';

export const messagesLoadingError = 'There was an issue loading your messages.';
export type CnuMessageCenterT = React.FC;

export const CnuMessageCenter: CnuMessageCenterT = () => {
  const [accountId] = useAtom(accountIdAtom);
  const [{ limit, offset: summaryOffset }, setSummaryPaginationValues] = useAtom(messageCenterPaginationAtom);
  const [_, setDetailPaginationValues] = useAtom(messageDetailPaginationAtom);

  const {
    count: messageCount,
    error: messagesError,
    hasMore,
    isLoading: messagesAreLoading,
    messages,
  } = useMessages({
    id: accountId || '',
    limit,
    offset: summaryOffset,
  });

  // Re-sync message detail pagination values every time message center page is loaded
  // or when offset or totalCount are updated.
  useEffect(
    () => {
      setDetailPaginationValues((detailValues) => {
        // Guard against unnecessary re-renders
        if (
          messageCount === detailValues.total
          && summaryOffset === detailValues.offset
          && hasMore === detailValues.hasMore
        ) { return detailValues; }

        return {
          ...detailValues,
          hasMore,
          offset: summaryOffset,
          total: messageCount || 0,
        };
      });
    },
    [
      hasMore,
      messageCount,
      setDetailPaginationValues,
      summaryOffset,
    ],
  );

  // Update message summary pagination values when message count changes.
  useEffect(
    () => {
      setSummaryPaginationValues((values) => {
        // Guard against unnecessary re-renders
        if (
          messagesAreLoading
          || (messageCount === values.total && hasMore === values.hasMore)
        ) { return values; }

        return { ...values, hasMore, total: messageCount || 0 };
      });
    },
    [
      hasMore,
      messageCount,
      messagesAreLoading,
      setSummaryPaginationValues,
    ],
  );

  // Track page views in Datadog.
  useEffect(
    () => {
      if (messagesAreLoading) return;

      datadogRum.addAction(messageCount ? 'CnuMessageCenterView' : 'CnuMessageCenterEmptyView');
    },
    [messageCount, messagesAreLoading],
  );

  let content;

  if (messagesError) {
    content = <Error displaySupportText message={messagesLoadingError} />;
  } else {
    let messageList;

    if (messagesAreLoading) {
      messageList = <CnuLoadingSpinner />;
    } else {
      messageList = <CnuMessageCenterListGroup messages={messages || []} />;
    }

    content = (
      <>
        <div className="top-nav">
          <h2 className="h3">Inbox</h2>

          <CnuPagination atom={messageCenterPaginationAtom} />
        </div>

        {messageList}

        <div className="bottom-nav">
          <CnuPagination atom={messageCenterPaginationAtom} />
        </div>
      </>
    );
  }

  return (
    <Container className="message-center d-flex flex-column rounded" hasBoxShadow>
      <h1 className="h2">Message Center</h1>

      {content}
    </Container>
  );
};
